import { get,  isArray , isEmpty, isNull, isUndefined } from "lodash";

import SnackbarUtils from "./SnackbarProvider";
import { CloudEnvironmentConfig } from "../constants/AppConfigs";
import { IS_LOCALHOST, REACT_APP_ENDPOINT_ENV } from "../constants/Config";
import Logger from "./logHandlers/Logger";
import { FormattedNumber } from "./PhoneNumber";
import { DropdownOptionKeys } from "../components/formComponents/constant/FormComponents";
import { alphabetSpaceAndHyphen, onlyNumAlphabetRegex } from "../constants/Regex";
import { MessageType } from "../constants/tostmsg";
import { showReloadRequiredModal } from "../App/common";
import { DisciplinePayload } from "../models/Authorization";
import { AxiosError } from "axios";
import { forbiddenStore } from "./forbiddenStore";
import { notFoundStore } from "./notFoundStore";
import { SECRET_KEY, ITERATIONS } from "../constants/FunctionalUtils";

/**
 * Method to execute all init calls post config Load
 *
 */
export const initAfterConfigLoad = (): void => {
  Logger.suppressConsole();
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Param value is mandatory
export const isNil = (value: unknown): value is null | undefined =>{
  if (value == undefined) {
    return true;
  }
  return false;
};

export const GetEnvVariables = () => {
  let envConfig  = {};

  if (IS_LOCALHOST && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const localConfig = require( "../configs/localConfig.json");
    const selectedEndpointEnv = REACT_APP_ENDPOINT_ENV;
    switch (selectedEndpointEnv) {
      case CloudEnvironmentConfig.SB1:
        envConfig = localConfig?.SB1 || {};
        break;
      case CloudEnvironmentConfig.QA1:
        envConfig = localConfig?.QA1 || {};
        break;
      case CloudEnvironmentConfig.DEV1:
        envConfig = localConfig?.DEV1 || {};
        break;
      case CloudEnvironmentConfig.SB2:
        envConfig = localConfig?.SB2 || {};
        break;
      case CloudEnvironmentConfig.DEV2:
        envConfig = localConfig?.DEV2 || {};
        break;
      case CloudEnvironmentConfig.QA2:
        envConfig = localConfig?.QA2 || {};
        break;
        default:
    }
  }
  return envConfig;
};

// Returns accessibility attributes (id and aria-controls) for tab components.

export const get404InitialData = (response: any, initialData: never[]= []) => {
  return isNull(response) || isEmpty(response) ? initialData : response;
};
export type ErrorResponsePayload = { status?: number } | null | undefined;
const msg = "Something went wrong!";
export const ErrorResponse = (response?: ErrorResponsePayload) => {
  if (!response || typeof response.status !== "number") {
    SnackbarUtils.warning(msg);
    return response;
  }
  switch (response.status) {
    case 422:
    case 400:
      return response;
    case 403:
      return response;
    case 404:
      return response;
    default:
      SnackbarUtils.warning(msg);
      break;
  }
};


export const getFormattedName = (str: string) => {
  return str
    .toLowerCase()
    .replace(/(^|\s|-)(\w)/g, (_, m1, m2) => m1 + m2.toUpperCase());
};

//below function used for apply css for tabs
export const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
    "data-testid": `patient-header-tabpanel-${index}`,
  };
};

export const isEmptyValue = (obj: { [key:string]: unknown }): boolean => {
  return Object.values(obj).every(
    (value) => value === null || value === "" || value === undefined
  );
};

/**
 *
 * @param phoneNumber string formatted/not formatted
 * @returns formatted phone number
 */

type NullableString = string | null | undefined;

export const getFormattedPhoneNo = (phoneNumber: NullableString) => {
  if (isNull(phoneNumber) || isUndefined(phoneNumber)) {
    return phoneNumber;
  } else {
    return FormattedNumber(phoneNumber.replace(/\D/g, ""), phoneNumber.length);
  }
};
//function to get the value at path of object
export const getValue = (
  inputObj: any, 
  nestedProp: string,
  defaultValue?: unknown
) => {
  if (isUndefined(defaultValue)) {
    defaultValue = null;
  }
  return get(inputObj, nestedProp, defaultValue);
};

/**
 * Reloads the current application page.
 *
 * This function triggers a full page reload using the browser's window.location.reload().
 */
export const reloadApplication = () => {
  window.location.href = window.location.pathname + "?t=" + Date.now().toString();
}

/**Checking length for different type of array in the application */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Param value is mandatory
export const length = (value: {length:number} | undefined) => {
  if (value !== undefined && value.length > 0) {
    return true;
  }
  return false;
};

/**
 *
 * @param payload api payload
 * @returns paylaod in query string format
 */
export const GetQueryString = (payload: DisciplinePayload) => {
  const QueryString = (Object.keys(payload) as Array<keyof DisciplinePayload>)
    .map((key) =>
      payload[key] !== null || undefined ? key + "=" + payload[key] : ""
    )
    .filter((str) => str !== "") // Filter out empty strings
    .join("&");

  return QueryString;
};

const extractDropdownOption = (item: any, keyNames: DropdownOptionKeys) => {
  const result: any = {};
  for (const [key, value] of Object.entries(keyNames)) {
    result[key] = item[value];
  }
  return result;
};


/**
 *
 * @param list array
 * @param keyNames dynamic key name {label: "key1", value: "key2"}
 * @returns array of dropdown options []{label: "label", value: "value"}
 */
export const getList = (list: any[], keyNames: DropdownOptionKeys) => {
  if (isArray(list)) {
    return list.map((v: any) => extractDropdownOption(v, keyNames));
  }
  return [];
};

/**
 * allow only numbers or string as per regex filter
 * @param {any}  e - input field value typed in
 * @param {string} type - allowed filter type - onlyNumber or onlyText or onlyTextAndSpace
 *
 */
export const handleInputEvent = (e: any, type: string | undefined) => {
  const initialValue = e.target.value;
  const selectionStart = e.target.selectionStart;

  let newSelectionStart = selectionStart;

  switch (type) {
    case "onlyNumber":
      e.target.value = initialValue.replace(/\D/g, "");
      break;
    case "onlyText":
      e.target.value = initialValue.replace(/[^a-zA-Z]/g, "");
      break;
    case "onlyTextAndSpace":
      e.target.value = initialValue.replace(/[^a-zA-Z\s]/g, "");
      break;
    case "onlyTextSpaceAndHyphen":
      e.target.value = initialValue.replace(alphabetSpaceAndHyphen, "");
      break;
    case "onlyNumAlphabetRegex":
      e.target.value = initialValue.replace(onlyNumAlphabetRegex, "");
      break;
  }
  if (initialValue !== e.target.value) {
    const removedCharsBeforeCursor = initialValue
      .substring(0, selectionStart)
      .split("")
      .filter((char: any) => !e.target.value.includes(char)).length;
    newSelectionStart = selectionStart - removedCharsBeforeCursor;
    e.target.setSelectionRange(newSelectionStart, newSelectionStart);
    e.stopPropagation();
  }
};
/**
 *
 * @param key
 * @param data
 * @returns
 */
export const setSessionStorage = (key: string, data: unknown) => {
  return sessionStorage.setItem(key, JSON.stringify(data));
};

/**
 *
 * @param key
 * @returns
 */
export const getSessionStorage = (key: string) => {
  const data = sessionStorage.getItem(key);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return null;
};

let isUnauthorized = false;

export const isUnauthorizedSession = () => isUnauthorized;
export const resetUnauthorizedSession = () => {
  isUnauthorized = false;
};

/**
 *
 * @param err
 */
export const handleUnauthorizedError = (err: AxiosError<unknown> ): boolean => {
  const status = getValue(err, "response.status");
 
  if (status === 401) {
    isUnauthorized = true;
 
    setSessionStorage("samlToken", null);
 
    showReloadRequiredModal(
      status,
      MessageType.UNAUTHORIZED_MESSAGE,
      MessageType.UNAUTHORIZED_ERROR_HEADER
    );
 
    return true;
  }
 
  if (status === 403) {
    forbiddenStore.setForbidden(true);
    return true;
  }
  if(status === 404){
    notFoundStore.setNotFound(true);
    return true;
  }
 
  return false;
};

/**
 *
 * @param buffer ArrayBuffer
 * @returns base64url string
 */
const arrayBufferToBase64Url = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

/**
 *
 * @param base64Url base64url string
 * @returns Uint8Array
 */
const base64UrlToUint8Array = (base64Url: string): Uint8Array => {
  const base64 = base64Url
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );

  const binary = atob(padded);

  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

/**
 *
 * @param secret encryption secret key
 * @param salt random salt in bytes
 * @returns derived CryptoKey for AES-GCM operations
 */
const deriveKey = async (
  secret: string,
  salt: Uint8Array
): Promise<CryptoKey> => {
  const encoder = new TextEncoder();

  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  // Creates an ArrayBuffer-backed copy
  const safeSalt = new Uint8Array(salt);

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: safeSalt,
      iterations: ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    {
      name: "AES-GCM",
      length: 256,
    },
    false,
    ["encrypt", "decrypt"]
  );
};

/**
 *
 * @param id id value to encrypt
 * @param secret optional secret key
 * @returns encrypted id in base64url format
 */
export const encryptId = async (
  id: string | number,
  secret: string = SECRET_KEY
): Promise<string> => {
  const encoder = new TextEncoder();

  // Random salt
  const salt = crypto.getRandomValues(new Uint8Array(16));

  // Random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const key = await deriveKey(secret, salt);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    encoder.encode(String(id))
  );

  // Store salt + IV + encrypted data together
  const result = new Uint8Array(
    salt.length + iv.length + encrypted.byteLength
  );

  result.set(salt, 0);
  result.set(iv, salt.length);
  result.set(
    new Uint8Array(encrypted),
    salt.length + iv.length
  );

  return arrayBufferToBase64Url(result.buffer);
};

/**
 *
 * @param encryptedId encrypted id in base64url format
 * @param secret optional secret key
 * @returns decrypted id as string
 */
export const decryptId = async (
  encryptedId: string,
  secret: string = SECRET_KEY
): Promise<string> => {
  const data = base64UrlToUint8Array(encryptedId);

  // Extract salt
  const salt = data.slice(0, 16);

  // Extract IV
  const iv = data.slice(16, 28);

  // Extract encrypted data
  const encrypted = data.slice(28);

  const key = await deriveKey(secret, salt);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    encrypted
  );

  return new TextDecoder().decode(decrypted);
};